// src/utils/resumeProfileAPI.js
import { API_ENDPOINTS } from '../config/api';
import { authFetch } from './authFetch';

const toBooleanOrUndefined = (value) => {
    if (value === true || value === false) return value;

    if (typeof value === "string") {
        const v = value.trim().toLowerCase();
        if (["true", "yes", "y", "1"].includes(v)) return true;
        if (["false", "no", "n", "0"].includes(v)) return false;
        if (v === "") return undefined;
    }

    if (value == null) return undefined;

    return undefined;
};

const booleanToYesNo = (value) => {
    if (value === true) return "Yes";
    if (value === false) return "No";

    if (typeof value === "string") {
        const v = value.trim().toLowerCase();
        if (["true", "yes", "y", "1"].includes(v)) return "Yes";
        if (["false", "no", "n", "0"].includes(v)) return "No";
    }

    return "";
};

const normalizeRaceEthnicity = (value) => {
    if (value == null) return "";
    const race = String(value).trim();
    if (!race || race.toLowerCase() === "select..." || race.toLowerCase() === "select") {
        return "";
    }
    return race;
};

const isNonEmpty = (v) => typeof v === "string" ? v.trim().length > 0 : v != null;

const hasAnyValue = (obj, keys) =>
    keys.some((k) => isNonEmpty(obj?.[k]));

/**
 * Maps resume form data to backend API format
 */
const mapResumeDataToAPIFormat = (resumeData) => {
    const sponsorshipBool = toBooleanOrUndefined(resumeData.sponsorshipRequired);
    const workAuthBool = toBooleanOrUndefined(resumeData.workAuthorization);

    return {
        user_id: resumeData.email || "",
        first_name: resumeData.firstName || "",
        last_name: resumeData.lastName || "",
        full_name: resumeData.fullName || "",
        email: resumeData.email || "",
        phone: resumeData.phone || "",
        address: resumeData.address || "",
        city: resumeData.city || "",
        state: resumeData.state || "",
        zip_code: resumeData.zipCode || "",

        // support both possible frontend keys
        linkedin_url: resumeData.linkedinUrl || resumeData.linkedin || "",
        professional_summary:
            resumeData.professionalSummary || resumeData.summary || "",

        skills: resumeData.skills || [],

        work_experience: (resumeData.experience || [])
            .filter((exp) =>
                hasAnyValue(exp, ["jobTitle", "title", "company", "companyName", "startDate", "endDate", "description", "responsibilities"])
            )
            .map((exp) => ({
                job_title: ((exp.jobTitle || exp.title || "") + "").trim(),
                company_name: ((exp.companyName || exp.company || "") + "").trim(),
                start_date: ((exp.startDate || exp.start_date || "") + "").trim(),
                end_date: ((exp.endDate || exp.end_date || "") + "").trim(),
                responsibilities: ((exp.responsibilities || exp.description || "") + "").trim(),
            })),

        education: (resumeData.education || [])
            .filter((edu) =>
                hasAnyValue(edu, ["degree", "fieldOfStudy", "school", "graduationDate"])
            )
            .map((edu) => ({
                degree: (edu.degree || "").trim(),
                field_of_study: (edu.fieldOfStudy || "").trim(),
                school: (edu.school || "").trim(),
                graduation_date: (edu.graduationDate || "").trim(),
            })),

        disability_status: resumeData.disabilityStatus || "",
        veteran_status: resumeData.veteranStatus || "",
        race_ethnicity: resumeData.raceEthnicity || "",
        gender: resumeData.gender || "",
        ...(sponsorshipBool !== undefined ? { sponsorship_required: sponsorshipBool } : {}),
        ...(workAuthBool !== undefined ? { work_authorization: workAuthBool } : {}),
    };
};


/**
 * Maps backend API response to resume form format
 */
const mapAPIResponseToResumeData = (apiData) => {
    return {
        firstName: apiData.first_name || '',
        lastName: apiData.last_name || '',
        fullName: apiData.full_name || '',
        email: apiData.email || '',
        phone: apiData.phone || '',
        address: apiData.address || '',
        city: apiData.city || '',
        state: apiData.state || '',
        zipCode: apiData.zip_code || '',

        linkedinUrl: apiData.linkedin_url || '',
        linkedin: apiData.linkedin_url || '',
        professionalSummary: apiData.professional_summary || '',
        summary: apiData.professional_summary || '',

        skills: apiData.skills || [],
        experience: (apiData.work_experience || []).map((exp) => ({
            title: exp.job_title || "",
            company: exp.company_name || exp.company || "",
            startDate: exp.start_date || "",
            endDate: exp.end_date || "",
            description: exp.responsibilities || "",
        })),
        education: (apiData.education || []).map((edu) => ({
            degree: edu.degree || '',
            fieldOfStudy: edu.field_of_study || '',
            school: edu.school || '',
            graduationDate: edu.graduation_date || ''
        })),

        disabilityStatus: apiData.disability_status ?? apiData.DisabilityStatus ?? '',
        veteranStatus: apiData.veteran_status ?? apiData.VeteranStatus ?? '',
        raceEthnicity: normalizeRaceEthnicity(
            apiData.race_ethnicity ?? apiData.RaceEthnicity ?? apiData.race ?? apiData.Race ?? ''
        ),
        gender: apiData.gender ?? apiData.Gender ?? '',
        sponsorshipRequired: booleanToYesNo(
            apiData.sponsorship_required ?? apiData.SponsorshipRequired
        ),
        workAuthorization: booleanToYesNo(
            apiData.work_authorization ?? apiData.WorkAuthorization
        ),
    };
};

/**
 * Save resume profile to backend (create or update)
 */
export const saveResumeProfile = async (userEmail, resumeData) => {
    try {
        const apiPayload = {
            user_id: userEmail,
            ...mapResumeDataToAPIFormat(resumeData)
        };


        // Use update_user_profile endpoint for partial updates
        const response = await authFetch(API_ENDPOINTS.UPDATE_USER_PROFILE, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(apiPayload)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.body || 'Failed to save resume profile');
        }

        const data = await response.json();

        return {
            success: true,
            message: 'Resume profile saved successfully',
            data: data
        };
    } catch (error) {
        console.error('Error saving resume profile:', error);
        return {
            success: false,
            message: error.message || 'Failed to save resume profile'
        };
    }
};

/**
 * Get resume profile from backend
 */
export const getResumeProfile = async (userEmail) => {
    try {
        const response = await authFetch(
            `${API_ENDPOINTS.GET_USER_PROFILE}?user_id=${encodeURIComponent(userEmail)}`
        );

        if (!response.ok) {
            if (response.status === 404) {
                // No profile found - this is okay, user hasn't created one yet
                return {
                    success: true,
                    hasProfile: false,
                    data: null
                };
            }
            throw new Error('Failed to fetch resume profile');
        }

        const responseData = await response.json();

        // --- Normalize shape: support both { body: {...} } and plain {...} ---
        let profileData = responseData.body ?? responseData;

        // If backend ever returns a JSON string, try to parse it
        if (typeof profileData === 'string') {
            try {
                profileData = JSON.parse(profileData);
            } catch (e) {
                console.error('Failed to parse profileData string:', e);
                return {
                    success: true,
                    hasProfile: false,
                    data: null
                };
            }
        }

        if (!profileData || typeof profileData !== 'object') {
            console.error('Unexpected profileData format:', profileData);
            return {
                success: true,
                hasProfile: false,
                data: null
            };
        }

        // Check if profile has any resume-ish data
        const hasResumeData =
            profileData.first_name ||
            profileData.last_name ||
            profileData.professional_summary ||
            (Array.isArray(profileData.skills) && profileData.skills.length > 0) ||
            (Array.isArray(profileData.work_experience) && profileData.work_experience.length > 0) ||
            (Array.isArray(profileData.education) && profileData.education.length > 0);

        if (!hasResumeData) {
            return {
                success: true,
                hasProfile: false,
                data: null
            };
        }

        const resumeData = mapAPIResponseToResumeData(profileData);

        return {
            success: true,
            hasProfile: true,
            data: resumeData
        };
    } catch (error) {
        console.error('Error fetching resume profile:', error);
        return {
            success: false,
            hasProfile: false,
            error: error.message
        };
    }
};

/**
 * Delete resume profile from backend (clears all resume fields)
 */
export const deleteResumeProfile = async (userEmail) => {
    try {
        // Send empty values for all resume fields to clear them
        const emptyResumeData = {
            user_id: userEmail,
            first_name: '',
            last_name: '',
            full_name: '',  // ΓåÉ ADDED THIS LINE
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            linkedin_url: '',
            professional_summary: '',
            skills: [],
            work_experience: [],
            education: [],
            disability_status: '',
            veteran_status: '',
            race_ethnicity: '',
            gender: '',
        };

        const response = await authFetch(API_ENDPOINTS.UPDATE_USER_PROFILE, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(emptyResumeData)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.body || 'Failed to delete resume profile');
        }

        return {
            success: true,
            message: 'Resume profile deleted successfully'
        };
    } catch (error) {
        console.error('Error deleting resume profile:', error);
        return {
            success: false,
            message: error.message || 'Failed to delete resume profile'
        };
    }
};

/**
 * Sync local storage with backend on login
 */
export const syncResumeProfileOnLogin = async (userEmail) => {
    try {
        // Get profile from backend
        const result = await getResumeProfile(userEmail);

        if (result.success && result.hasProfile) {
            // Save to local storage
            const storageKey = `jobque_resume_data_${userEmail}`;
            localStorage.setItem(storageKey, JSON.stringify(result.data));

            // Also sync with extension
            window.postMessage(
                { type: 'RESUME_AUTOFILL_SAVE', payload: result.data },
                '*'
            );

            return {
                success: true,
                hasProfile: true,
                data: result.data
            };
        }

        return {
            success: true,
            hasProfile: false,
            data: null
        };
    } catch (error) {
        console.error('Error syncing resume profile on login:', error);
        return {
            success: false,
            error: error.message
        };
    }
};